import { useSelector } from 'react-redux'
import {
  getTemplateById,
  RenderObjectType,
  selectAgreementConfigs,
  selectAllAgreementTemplates,
} from '@user/store/agreements'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CreateAgreementFormPacketModel,
  CreateAgreementTemplateDetails,
} from '@user/components/Agreements/AgreementFormPacketDialog/typings'
import {
  ENTITY_TYPES,
  getServiceLocationWithContactName,
  updateLoadingState,
  useAppDispatch,
} from '@sales-center/common'
import {
  GroupedServices,
  ILeadOppProduct,
  selectAllLeadOppProducts,
} from '@user/store/leadOppProducts'
import { useFormikContext } from 'formik'
import { selectAllServiceLocations } from '@user/store/entityLocations'
import { IAttachmentToAgreementDTO } from '@user/store/attachments'

export function useAgreementCreateActions(
  entityId: number,
  entityType: ENTITY_TYPES,
) {
  const { setValues, values } =
    useFormikContext<CreateAgreementFormPacketModel>()

  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>(
    'success',
  )
  const [agreement, setAgreement] = useState<
    CreateAgreementTemplateDetails | undefined
  >(undefined)
  const dispatch = useAppDispatch()
  const templates = useSelector(selectAllAgreementTemplates)
  const tempServices = useSelector(selectAllLeadOppProducts)
  const [agreementServices, setAgreementServices] = useState<
    (ILeadOppProduct & { disabled?: boolean })[]
  >([])
  const serviceLocations = useSelector(selectAllServiceLocations)
  const agreementConfigs = useSelector(selectAgreementConfigs)

  const groupServicesByLocation = (
    services: ILeadOppProduct[],
  ): GroupedServices[] => {
    return services.reduce((acc: GroupedServices[], service) => {
      const { serviceLocationId } = service
      if (!serviceLocationId) return acc

      let group = acc.find(
        (item) => item.serviceLocationId === serviceLocationId,
      )

      if (!group) {
        group = { serviceLocationId, services: [] }
        acc.push(group)
      }

      group.services.push(service)

      return acc
    }, [] as GroupedServices[])
  }

  const services: GroupedServices[] = useMemo(
    () => groupServicesByLocation(tempServices),
    [tempServices],
  )

  useEffect(() => {
    if (values.templates?.filter((item) => 'templateId' in item).length === 0) {
      setAgreementServices(services.flatMap((group) => group.services))
    } else {
      if (agreement && agreement.isTemplateAlreadyAdded) {
        const mappedServices = agreementServices
          .map((service) => {
            const isServiceAvailable = tempServices.find(
              (row) => row.id === service.id,
            )
            if (isServiceAvailable) {
              return service
            }
            return null
          })
          .filter((row) => row !== null)
        setAgreementServices(mappedServices)
      } else {
        setAgreementServices([])
      }
    }
  }, [services, values.templates])

  const handleTemplateChange = useCallback(
    async (value: CreateAgreementTemplateDetails | null | undefined) => {
      if (value?.templateId) {
        if (value.isTemplateAlreadyAdded) {
          const existingTemplate: CreateAgreementTemplateDetails =
            values.templates.find(
              (item: CreateAgreementTemplateDetails) =>
                item.templateId === value.templateId,
            ) as CreateAgreementTemplateDetails
          //ensure the service is in the entity services
          const selectedCanAddServices = agreementServices?.filter((row) => {
            return !existingTemplate?.services?.some(
              (service) => service.id === row.id,
            )
          })

          const filteredServices = existingTemplate?.services
            ?.filter((existingService) =>
              services
                ?.flatMap((row) => row?.services)
                ?.some((service) => service.id === existingService.id),
            )
            ?.map((service) => ({
              ...service,
              disabled: true,
            }))

          const servicesList = [
            ...(filteredServices || []),
            ...selectedCanAddServices,
          ]

          setAgreementServices(servicesList)
        } else {
          setAgreementServices(
            //when changing a the template after selecting an existing template, remove the disabled property
            agreementServices?.map((row) => {
              delete row?.disabled
              return { ...row }
            }),
          )
        }

        setAgreement({
          templateFormName: value.templateFormName,
          templateId: value.templateId,
          dirty: true,
          isTemplateAlreadyAdded: value.isTemplateAlreadyAdded,
        })
      } else {
        setAgreement(undefined)
        setAgreementServices([])
      }
    },
    [values.templates, agreementServices],
  )

  const onChangeService = useCallback(
    (service: ILeadOppProduct) => {
      const serviceIndex = agreementServices.findIndex(
        (item) => item.id === service.id,
      )
      const tempServices = [...agreementServices]
      if (serviceIndex === -1) {
        setAgreementServices([...tempServices, service])
      } else {
        setAgreementServices(
          tempServices.filter((item) => item.id !== service.id),
        )
      }
    },
    [agreementServices],
  )

  const toggleServices = useCallback(
    (action: string) => {
      if (action === 'SELECT_ALL') {
        if (agreement?.isTemplateAlreadyAdded) {
          setAgreementServices(
            services
              .flatMap((group) => group.services)
              ?.map((row) => {
                const existingService = agreementServices.find(
                  (item) => item.id === row.id,
                )
                return {
                  ...row,
                  disabled: existingService?.disabled || false,
                }
              }),
          )
        } else {
          setAgreementServices(services.flatMap((group) => group.services))
        }
      } else {
        if (agreement?.isTemplateAlreadyAdded) {
          setAgreementServices(
            agreementServices.filter((item) => item.disabled),
          )
        } else {
          setAgreementServices([])
        }
      }
    },
    [services, agreement],
  )

  const getServiceLocationName = useCallback(
    (locationId: string) => {
      const location = serviceLocations.find(
        (sv) => sv.id == Number(locationId),
      )
      return location ? getServiceLocationWithContactName(location) || '' : ''
    },
    [serviceLocations],
  )

  const createAgreement = useCallback(async () => {
    if (agreement?.templateId) {
      setStatus('pending')
      const response = await dispatch(
        getTemplateById({
          id: agreement.templateId,
          proposalEntityId: entityId,
          proposalEntityType: entityType,
          dsbServiceIds: agreementServices?.map((x) => x.id),
        }),
      )

      if (getTemplateById.fulfilled.match(response)) {
        setStatus('success')
        const {
          dynamicServiceBlock,
          numOfServices,
          templateName,
          isCardCaptureEnabled,
          willDynamicServiceBlockFit,
        } = response.payload

        const template = {
          templateFormName: templateName,
          templateId: agreement.templateId,
          isCardCaptureEnabled: isCardCaptureEnabled,
          dynamicServiceBlock,
          numOfServices,
          includeDetail: dynamicServiceBlock && !willDynamicServiceBlockFit,
          willDynamicServiceBlockFit,
          dirty: true,
          services: agreementServices,
          groupedServices: groupServicesByLocation(agreementServices),
        }

        //if the template is already added, update the template
        if (agreement.isTemplateAlreadyAdded) {
          const existingTemplateIndex = values.templates.findIndex(
            (item: CreateAgreementTemplateDetails) =>
              item.templateId === agreement.templateId,
          )
          const tempTemplates = [...values.templates]
          tempTemplates[existingTemplateIndex] = template

          setValues((v) => {
            return {
              ...v,
              templates: [...tempTemplates],
            }
          })
        } else {
          //if the template is not added, add the template
          setValues((v) => {
            return {
              ...v,
              templates: [...v.templates, ...[template]],
            }
          })
        }
        setAgreement(undefined)
        setAgreementServices([])
      } else setStatus('failed')
    }
  }, [
    agreement?.templateId,
    agreementServices,
    dispatch,
    entityId,
    entityType,
    setValues,
  ])

  const handleUpdateAgreement = useCallback(async () => {
    if (agreementConfigs) {
      dispatch(updateLoadingState('visible'))
      if (
        agreementConfigs?.agreementRenderObjects &&
        agreementConfigs?.agreementRenderObjects?.length > 0
      ) {
        const savedTemplates = await Promise.all(
          agreementConfigs?.agreementRenderObjects?.map(
            async (agreementRenderObject) => {
              if (
                agreementRenderObject?.renderObjectType ===
                RenderObjectType.Attachment
              ) {
                return {
                  attachmentType: agreementRenderObject?.fileType,
                  createdDate: agreementRenderObject?.fileCreatedDate,
                  displayName: agreementRenderObject?.name,
                  fileSize: agreementRenderObject?.fileSize,
                  id: agreementRenderObject?.id,
                  isSaleCenterAttachment:
                    !agreementRenderObject?.isFileFromCoreApp,
                  isCompletedFormPdf: agreementRenderObject?.isCompletedFormPdf,
                } as IAttachmentToAgreementDTO
              }
              if (
                agreementRenderObject?.renderObjectType ===
                RenderObjectType.Template
              ) {
                const findService = agreementConfigs?.templateServices?.find(
                  (row) =>
                    String(row?.templateId) === agreementRenderObject?.id,
                )
                if (findService) {
                  const { templateId, dynamicServiceBlockMetadata } =
                    findService
                  const response = await dispatch(
                    getTemplateById({
                      id: templateId,
                      proposalEntityId: entityId,
                      proposalEntityType: entityType,
                      dsbServiceIds: dynamicServiceBlockMetadata.serviceIds,
                    }),
                  )

                  if (getTemplateById.fulfilled.match(response)) {
                    const {
                      dynamicServiceBlock,
                      numOfServices,
                      templateName,
                      isCardCaptureEnabled,
                      willDynamicServiceBlockFit,
                    } = response.payload

                    const templateServices =
                      dynamicServiceBlockMetadata.serviceIds
                        .map((id) => tempServices.find((x) => x.id === id))
                        .filter(
                          (service): service is ILeadOppProduct =>
                            service !== undefined,
                        )

                    return {
                      templateFormName: templateName,
                      templateId: templateId,
                      isCardCaptureEnabled,
                      dynamicServiceBlock,
                      numOfServices,
                      includeDetail: dynamicServiceBlockMetadata.includeDetail,
                      willDynamicServiceBlockFit,
                      dirty: true,
                      services: templateServices,
                      groupedServices:
                        groupServicesByLocation(templateServices),
                    } as CreateAgreementTemplateDetails
                  } else return null
                }
              }
              return null
            },
          ),
        )

        dispatch(updateLoadingState('hidden'))

        setValues((v) => ({
          ...v,
          templates: savedTemplates.filter((item) => item !== null),
        }))
      } else {
        const savedTemplates = await Promise.all(
          agreementConfigs.templateServices.map(async (item) => {
            const { templateId, dynamicServiceBlockMetadata } = item

            const response = await dispatch(
              getTemplateById({
                id: templateId,
                proposalEntityId: entityId,
                proposalEntityType: entityType,
                dsbServiceIds: dynamicServiceBlockMetadata.serviceIds,
              }),
            )

            if (getTemplateById.fulfilled.match(response)) {
              const {
                dynamicServiceBlock,
                numOfServices,
                templateName,
                isCardCaptureEnabled,
                willDynamicServiceBlockFit,
              } = response.payload

              const templateServices = dynamicServiceBlockMetadata.serviceIds
                .map((id) => tempServices.find((x) => x.id === id))
                .filter(
                  (service): service is ILeadOppProduct =>
                    service !== undefined,
                )

              return {
                templateFormName: templateName,
                templateId: templateId,
                isCardCaptureEnabled,
                dynamicServiceBlock,
                numOfServices,
                includeDetail: dynamicServiceBlockMetadata.includeDetail,
                willDynamicServiceBlockFit,
                dirty: true,
                services: templateServices,
                groupedServices: groupServicesByLocation(templateServices),
              }
            } else return null
          }),
        )

        dispatch(updateLoadingState('hidden'))

        setValues((v) => ({
          ...v,
          templates: savedTemplates.filter((item) => item !== null),
        }))
      }
    }
  }, [
    agreementConfigs,
    agreementServices,
    dispatch,
    entityId,
    entityType,
    setValues,
    tempServices,
  ])

  useEffect(() => {
    handleUpdateAgreement()
  }, [agreementConfigs])

  //group the templates by existing and new and existing on top of list
  const groupedTemplates = useMemo(() => {
    const existingTemplates = templates
      ?.sort((a, b) => a.templateFormName.localeCompare(b.templateFormName))
      .filter((template) => {
        return values.templates.some(
          (item: CreateAgreementTemplateDetails) =>
            item.templateId === template.templateId,
        )
      })
      ?.map((row) => ({ ...row, isTemplateAlreadyAdded: true }))

    const newTemplates = templates
      ?.sort((a, b) => a.templateFormName.localeCompare(b.templateFormName))
      .filter((template) => {
        return values.templates.every(
          (item: CreateAgreementTemplateDetails) =>
            item.templateId !== template.templateId,
        )
      })
      ?.map((row) => ({ ...row, isTemplateAlreadyAdded: false }))

    if (existingTemplates.length === 0) {
      return templates?.sort((a, b) =>
        a.templateFormName.localeCompare(b.templateFormName),
      )
    }

    return [...existingTemplates, ...newTemplates]
  }, [values.templates, templates])

  return {
    templates: groupedTemplates,
    handleTemplateChange,
    loading: status === 'pending',
    services,
    agreement,
    agreementServices,
    onChangeService,
    toggleServices,
    getServiceLocationName,
    createAgreement,
    formTemplates: values.templates,
  }
}
